import omit from 'lodash/omit';
import { pathOr } from 'ramda';

import { types as sdkTypes } from '../../util/sdkLoader';
import { storableError } from '../../util/errors';
import * as log from '../../util/log';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { createResourceLocatorString } from '../../util/routes';
import routeConfiguration from '../../routeConfiguration';
import { createSlug } from '../../util/urlHelpers';
import { entriesAvailabilityPlanTimeBased } from './constants';
import { utcTimeZone } from '../../util/dates';
import {
  createStripeAccount,
  updateStripeAccount,
  fetchStripeAccount,
} from '../../ducks/stripeConnectAccount.duck';
import { fetchCurrentUser, markDatadogRumProviderEligible } from '../../ducks/user.duck';
import { LISTING_STATE_PUBLISHED } from '../../constants/listing';

const { UUID } = sdkTypes;

const requestAction = (actionType) => (params) => ({ type: actionType, payload: { params } });

const successAction = (actionType) => (result) => ({ type: actionType, payload: result.data });

const errorAction = (actionType) => (error) => ({ type: actionType, payload: error, error: true });

// ================ Action types ================ //

export const MARK_TAB_UPDATED = 'app/EditListingPage/MARK_TAB_UPDATED';
export const CLEAR_UPDATED_TAB = 'app/EditListingPage/CLEAR_UPDATED_TAB';

export const CREATE_LISTING_DRAFT_REQUEST = 'app/EditListingPage/CREATE_LISTING_DRAFT_REQUEST';
export const CREATE_LISTING_DRAFT_SUCCESS = 'app/EditListingPage/CREATE_LISTING_DRAFT_SUCCESS';
export const CREATE_LISTING_DRAFT_ERROR = 'app/EditListingPage/CREATE_LISTING_DRAFT_ERROR';

export const PUBLISH_LISTING_REQUEST = 'app/EditListingPage/PUBLISH_LISTING_REQUEST';
export const PUBLISH_LISTING_SUCCESS = 'app/EditListingPage/PUBLISH_LISTING_SUCCESS';
export const PUBLISH_LISTING_ERROR = 'app/EditListingPage/PUBLISH_LISTING_ERROR';

export const UPDATE_LISTING_REQUEST = 'app/EditListingPage/UPDATE_LISTING_REQUEST';
export const UPDATE_LISTING_SUCCESS = 'app/EditListingPage/UPDATE_LISTING_SUCCESS';
export const UPDATE_LISTING_ERROR = 'app/EditListingPage/UPDATE_LISTING_ERROR';

export const SHOW_LISTINGS_REQUEST = 'app/EditListingPage/SHOW_LISTINGS_REQUEST';
export const SHOW_LISTINGS_SUCCESS = 'app/EditListingPage/SHOW_LISTINGS_SUCCESS';
export const SHOW_LISTINGS_ERROR = 'app/EditListingPage/SHOW_LISTINGS_ERROR';

export const UPLOAD_IMAGE_REQUEST = 'app/EditListingPage/UPLOAD_IMAGE_REQUEST';
export const UPLOAD_IMAGE_SUCCESS = 'app/EditListingPage/UPLOAD_IMAGE_SUCCESS';
export const UPLOAD_IMAGE_ERROR = 'app/EditListingPage/UPLOAD_IMAGE_ERROR';

export const UPDATE_IMAGE_ORDER = 'app/EditListingPage/UPDATE_IMAGE_ORDER';

export const REMOVE_LISTING_IMAGE = 'app/EditListingPage/REMOVE_LISTING_IMAGE';

export const SAVE_PAYOUT_DETAILS_REQUEST = 'app/EditListingPage/SAVE_PAYOUT_DETAILS_REQUEST';
export const SAVE_PAYOUT_DETAILS_SUCCESS = 'app/EditListingPage/SAVE_PAYOUT_DETAILS_SUCCESS';
export const SAVE_PAYOUT_DETAILS_ERROR = 'app/EditListingPage/SAVE_PAYOUT_DETAILS_ERROR';

export const CHECK_CAN_PUBLISH_LISTING_REQUEST =
  'app/EditListingPage/CHECK_CAN_PUBLISH_LISTING_REQUEST';
export const CHECK_CAN_PUBLISH_LISTING_SUCCESS =
  'app/EditListingPage/CHECK_CAN_PUBLISH_LISTING_SUCCESS';
export const CHECK_CAN_PUBLISH_LISTING_ERROR =
  'app/EditListingPage/CHECK_CAN_PUBLISH_LISTING_ERROR';

// ================ Reducer ================ //

const initialState = {
  // Error instance placeholders for each endpoint
  createListingDraftError: null,
  publishingListing: null,
  publishListingError: null,
  updateListingError: null,
  showListingsError: null,
  uploadImageError: null,
  createListingDraftInProgress: false,
  submittedListingId: null,
  redirectToListing: false,
  images: {},
  imageOrder: [],
  removedImageIds: [],
  listingDraft: null,
  updatedTab: null,
  updateInProgress: false,
  payoutDetailsSaveInProgress: false,
  payoutDetailsSaved: false,
  showListingInProgress: false,
  checkCanPublishListingInProgress: false,
  checkCanPublishListingError: null,
  canPublishListing: true,
  canPublishListingChecked: false,
  maxListings: 1,
  publishedListingsCount: 0,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case MARK_TAB_UPDATED:
      return { ...state, updatedTab: payload };
    case CLEAR_UPDATED_TAB:
      return { ...state, updatedTab: null, updateListingError: null };

    case CREATE_LISTING_DRAFT_REQUEST:
      return {
        ...state,
        createListingDraftInProgress: true,
        createListingDraftError: null,
        submittedListingId: null,
        listingDraft: null,
      };
    case CREATE_LISTING_DRAFT_SUCCESS:
      return {
        ...state,
        createListingDraftInProgress: false,
        submittedListingId: payload.data.id,
        listingDraft: payload.data,
      };
    case CREATE_LISTING_DRAFT_ERROR:
      return {
        ...state,
        createListingDraftInProgress: false,
        createListingDraftError: payload,
      };

    case PUBLISH_LISTING_REQUEST:
      return {
        ...state,
        publishingListing: payload.listingId,
        publishListingError: null,
      };
    case PUBLISH_LISTING_SUCCESS:
      return {
        ...state,
        redirectToListing: true,
        publishingListing: null,
        createListingDraftError: null,
        updateListingError: null,
        showListingsError: null,
        uploadImageError: null,
        createListingDraftInProgress: false,
        updateInProgress: false,
      };
    case PUBLISH_LISTING_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        publishingListing: null,
        publishListingError: {
          listingId: state.publishingListing,
          error: payload,
        },
      };
    }

    case UPDATE_LISTING_REQUEST:
      return { ...state, updateInProgress: true, updateListingError: null };
    case UPDATE_LISTING_SUCCESS:
      return { ...state, updateInProgress: false };
    case UPDATE_LISTING_ERROR:
      return { ...state, updateInProgress: false, updateListingError: payload };

    case SHOW_LISTINGS_REQUEST:
      return { ...state, showListingInProgress: true, showListingsError: null };
    case SHOW_LISTINGS_SUCCESS:
      return { ...initialState, showListingInProgress: false };
    case SHOW_LISTINGS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        showListingInProgress: false,
        showListingsError: payload,
        redirectToListing: false,
      };

    case UPLOAD_IMAGE_REQUEST: {
      // payload.params: { id: 'tempId', file }
      const images = {
        ...state.images,
        [payload.params.id]: { ...payload.params },
      };
      return {
        ...state,
        images,
        imageOrder: state.imageOrder.concat([payload.params.id]),
        uploadImageError: null,
      };
    }
    case UPLOAD_IMAGE_SUCCESS: {
      // payload.params: { id: 'tempId', imageId: 'some-real-id'}
      const { id, imageId } = payload;
      const file = state.images[id].file;
      const images = { ...state.images, [id]: { id, imageId, file } };
      return { ...state, images };
    }
    case UPLOAD_IMAGE_ERROR: {
      // eslint-disable-next-line no-console
      const { id, error } = payload;
      const imageOrder = state.imageOrder.filter((i) => i !== id);
      const images = omit(state.images, id);
      return { ...state, imageOrder, images, uploadImageError: error };
    }
    case UPDATE_IMAGE_ORDER:
      return { ...state, imageOrder: payload.imageOrder };

    case REMOVE_LISTING_IMAGE: {
      const id = payload.imageId;

      // Only mark the image removed if it hasn't been added to the
      // listing already
      const removedImageIds = state.images[id]
        ? state.removedImageIds
        : state.removedImageIds.concat(id);

      // Always remove from the draft since it might be a new image to
      // an existing listing.
      const images = omit(state.images, id);
      const imageOrder = state.imageOrder.filter((i) => i !== id);

      return { ...state, images, imageOrder, removedImageIds };
    }

    case SAVE_PAYOUT_DETAILS_REQUEST:
      return { ...state, payoutDetailsSaveInProgress: true };
    case SAVE_PAYOUT_DETAILS_ERROR:
      return { ...state, payoutDetailsSaveInProgress: false };
    case SAVE_PAYOUT_DETAILS_SUCCESS:
      return { ...state, payoutDetailsSaveInProgress: false, payoutDetailsSaved: true };

    case CHECK_CAN_PUBLISH_LISTING_REQUEST:
      return {
        ...state,
        checkCanPublishListingInProgress: true,
        checkCanPublishListingError: null,
        canPublishListingChecked: false,
      };
    case CHECK_CAN_PUBLISH_LISTING_SUCCESS:
      return {
        ...state,
        checkCanPublishListingInProgress: false,
        checkCanPublishListingError: null,
        canPublishListing: payload.canPublishListing,
        canPublishListingChecked: true,
        maxListings: payload.maxListings,
        publishedListingsCount: payload.publishedListingsCount,
      };
    case CHECK_CAN_PUBLISH_LISTING_ERROR:
      return {
        ...state,
        checkCanPublishListingInProgress: false,
        checkCanPublishListingError: payload,
        canPublishListing: true,
        canPublishListingChecked: true,
      };

    default:
      return state;
  }
}

// ================ Selectors ================ //

// ================ Action creators ================ //

export const markTabUpdated = (tab) => ({
  type: MARK_TAB_UPDATED,
  payload: tab,
});

export const clearUpdatedTab = () => ({
  type: CLEAR_UPDATED_TAB,
});

export const updateImageOrder = (imageOrder) => ({
  type: UPDATE_IMAGE_ORDER,
  payload: { imageOrder },
});

export const removeListingImage = (imageId) => ({
  type: REMOVE_LISTING_IMAGE,
  payload: { imageId },
});

// All the action creators that don't have the {Success, Error} suffix
// take the params object that the corresponding SDK endpoint method
// expects.

// SDK method: ownListings.create
export const createListingDraft = requestAction(CREATE_LISTING_DRAFT_REQUEST);
export const createListingDraftSuccess = successAction(CREATE_LISTING_DRAFT_SUCCESS);
export const createListingDraftError = errorAction(CREATE_LISTING_DRAFT_ERROR);

// SDK method: ownListings.publish
export const publishListing = requestAction(PUBLISH_LISTING_REQUEST);
export const publishListingSuccess = successAction(PUBLISH_LISTING_SUCCESS);
export const publishListingError = errorAction(PUBLISH_LISTING_ERROR);

// SDK method: ownListings.update
export const updateListing = requestAction(UPDATE_LISTING_REQUEST);
export const updateListingSuccess = successAction(UPDATE_LISTING_SUCCESS);
export const updateListingError = errorAction(UPDATE_LISTING_ERROR);

// SDK method: ownListings.show
export const showListings = requestAction(SHOW_LISTINGS_REQUEST);
export const showListingsSuccess = successAction(SHOW_LISTINGS_SUCCESS);
export const showListingsError = errorAction(SHOW_LISTINGS_ERROR);

// SDK method: images.upload
export const uploadImage = requestAction(UPLOAD_IMAGE_REQUEST);
export const uploadImageSuccess = successAction(UPLOAD_IMAGE_SUCCESS);
export const uploadImageError = errorAction(UPLOAD_IMAGE_ERROR);

export const savePayoutDetailsRequest = requestAction(SAVE_PAYOUT_DETAILS_REQUEST);
export const savePayoutDetailsSuccess = successAction(SAVE_PAYOUT_DETAILS_SUCCESS);
export const savePayoutDetailsError = errorAction(SAVE_PAYOUT_DETAILS_ERROR);

export const checkCanPublishListingRequest = () => ({
  type: CHECK_CAN_PUBLISH_LISTING_REQUEST,
});

export const checkCanPublishListingSuccess = (payload) => ({
  type: CHECK_CAN_PUBLISH_LISTING_SUCCESS,
  payload,
});

export const checkCanPublishListingError = errorAction(CHECK_CAN_PUBLISH_LISTING_ERROR);

// ================ Thunk ================ //

const normalizeMaxListings = (maxListings) => {
  const parsedValue = Number(maxListings);
  const isValidMaxListings = Number.isFinite(parsedValue) && parsedValue >= 1;
  return isValidMaxListings ? Math.floor(parsedValue) : 1;
};

const getMaxListings = (currentUser) => {
  const maxListings = pathOr(
    1,
    ['attributes', 'profile', 'protectedData', 'maxListings'],
    currentUser
  );
  return normalizeMaxListings(maxListings);
};

const getPublishedListingsCount = async (sdk) => {
  const perPage = 1;
  const page = 1;
  const response = await sdk.ownListings.query({
    page,
    states: [LISTING_STATE_PUBLISHED],
    per_page: perPage,
  });

  return pathOr(0, ['data', 'meta', 'totalItems'], response);
};

export const checkCanPublishListing = () => async (dispatch, getState, sdk) => {
  dispatch(checkCanPublishListingRequest());

  const currentUser = pathOr(null, ['user', 'currentUser'], getState());
  const maxListings = getMaxListings(currentUser);

  try {
    const publishedListingsCount = await getPublishedListingsCount(sdk);
    const canPublishListing = publishedListingsCount < maxListings;
    const payload = {
      maxListings,
      publishedListingsCount,
      canPublishListing,
    };
    dispatch(checkCanPublishListingSuccess(payload));
    return payload;
  } catch (e) {
    log.error(e, 'check-can-publish-listing-failed');
    dispatch(checkCanPublishListingError(storableError(e)));
    return {
      maxListings,
      publishedListingsCount: 0,
      canPublishListing: true,
    };
  }
};

export function requestShowCongratPage(actionPayload, history) {
  return (dispatch, getState, sdk) => {
    dispatch(showListings(actionPayload));
    return sdk.ownListings
      .show(actionPayload)
      .then((response) => {
        const routes = routeConfiguration();
        const id = pathOr('', ['data', 'data', 'id', 'uuid'], response);
        const slug = createSlug(pathOr('', ['data', 'data', 'attributes', 'title'], response));

        history.push(createResourceLocatorString('CongratPoolPage', routes, {}, { id, slug }));
        dispatch(showListingsSuccess(response));
        return response;
      })
      .catch((e) => dispatch(showListingsError(storableError(e))));
  };
}

export function requestShowListing(actionPayload) {
  return (dispatch, getState, sdk) => {
    dispatch(showListings(actionPayload));
    return sdk.ownListings
      .show(actionPayload)
      .then((response) => {
        // EditListingPage fetches new listing data, which also needs to be added to global data
        dispatch(addMarketplaceEntities(response));
        // In case of success, we'll clear state.EditListingPage (user will be redirected away)
        dispatch(showListingsSuccess(response));
        return response;
      })
      .catch((e) => dispatch(showListingsError(storableError(e))));
  };
}

export const addAvailabilityPlanToListingPayload = (payload) => ({
  ...payload,
  availabilityPlan: {
    type: 'availability-plan/time',
    timezone: utcTimeZone,
    entries: entriesAvailabilityPlanTimeBased,
  },
});

export function requestCreateListingDraft(data) {
  return (dispatch, getState, sdk) => {
    dispatch(createListingDraft(data));

    const queryParams = {
      expand: true,
      include: ['author', 'images'],
      'fields.image': ['variants.landscape-crop', 'variants.landscape-crop2x'],
    };

    const listingPayload = addAvailabilityPlanToListingPayload(data);
    return sdk.ownListings
      .createDraft(listingPayload, queryParams)
      .then((response) => {
        // Add the created listing to the marketplace data
        dispatch(addMarketplaceEntities(response));

        // Modify store to understand that we have created listing and can redirect away
        dispatch(createListingDraftSuccess(response));
        // Fetch listing data so that redirection is smooth

        return response;
      })
      .catch((e) => {
        log.error(e, 'create-listing-draft-failed', { listingData: data });
        return dispatch(createListingDraftError(storableError(e)));
      });
  };
}

export const requestPublishListingDraft =
  (listingId, history) => async (dispatch, getState, sdk) => {
    const checkCanPublishResult = await dispatch(checkCanPublishListing());
    const canPublishListing = pathOr(true, ['EditListingPage', 'canPublishListing'], getState());

    if (!canPublishListing) {
      return {
        ...checkCanPublishResult,
        blockedByMaxListings: true,
      };
    }

    const wasProvider = pathOr(false, ['user', 'currentUserHasPublishedListings'], getState());

    dispatch(publishListing(listingId));

    return sdk.ownListings
      .publishDraft({ id: listingId }, { expand: true })
      .then((response) => {
        // Add the created listing to the marketplace data
        dispatch(addMarketplaceEntities(response));
        dispatch(publishListingSuccess(response));
        if (!wasProvider) {
          dispatch(markDatadogRumProviderEligible());
        }
        dispatch(
          requestShowCongratPage(
            {
              id: listingId.uuid,
              include: ['author', 'images'],
              'fields.image': ['variants.landscape-crop', 'variants.landscape-crop2x'],
            },
            history
          )
        );
        return response;
      })
      .catch((e) => {
        const error = storableError(e);
        dispatch(publishListingError(error));
        return {
          blockedByMaxListings: false,
          error,
        };
      });
  };

// Images return imageId which we need to map with previously generated temporary id
export function requestImageUpload(actionPayload) {
  return (dispatch, getState, sdk) => {
    const id = actionPayload.id;
    dispatch(uploadImage(actionPayload));
    return sdk.images
      .upload({ image: actionPayload.file })
      .then((resp) => dispatch(uploadImageSuccess({ data: { id, imageId: resp.data.data.id } })))
      .catch((e) => dispatch(uploadImageError({ id, error: storableError(e) })));
  };
}

// Update the given tab of the wizard with the given data. This saves
// the data to the listing, and marks the tab updated so the UI can
// display the state.
export function requestUpdateListing(tab, data) {
  return (dispatch, getState, sdk) => {
    dispatch(updateListing(data));
    const { id } = data;
    let updateResponse;
    return sdk.ownListings
      .update(data)
      .then((response) => {
        updateResponse = response;
        const payload = {
          id,
          include: ['author', 'images'],
          'fields.image': ['variants.landscape-crop', 'variants.landscape-crop2x'],
        };
        return dispatch(requestShowListing(payload));
      })
      .then(() => {
        dispatch(markTabUpdated(tab));
        dispatch(updateListingSuccess(updateResponse));
        return updateResponse;
      })
      .catch((e) => {
        log.error(e, 'update-listing-failed', { listingData: data });
        return dispatch(updateListingError(storableError(e)));
      });
  };
}

export const savePayoutDetails = (values, isUpdateCall) => (dispatch, getState, sdk) => {
  const upsertThunk = isUpdateCall ? updateStripeAccount : createStripeAccount;
  dispatch(savePayoutDetailsRequest());

  return dispatch(upsertThunk(values, { expand: true }))
    .then((response) => {
      dispatch(savePayoutDetailsSuccess());
      return response;
    })
    .catch(() => dispatch(savePayoutDetailsError()));
};

// loadData is run for each tab of the wizard. When editing an
// existing listing, the listing must be fetched first.
export const loadData = (params) => (dispatch, getState, sdk) => {
  dispatch(clearUpdatedTab());
  const { id, type } = params;

  if (type === 'new') {
    // No need to listing data when creating a new listing
    return Promise.all([dispatch(fetchCurrentUser())])
      .then((response) => {
        const currentUser = getState().user.currentUser;
        if (currentUser?.stripeAccount) {
          dispatch(fetchStripeAccount());
        }
        return dispatch(checkCanPublishListing()).then(() => response);
      })
      .catch((e) => {
        throw e;
      });
  }

  const payload = {
    id: new UUID(id),
    include: ['author', 'images'],
    'fields.image': ['variants.landscape-crop', 'variants.landscape-crop2x'],
  };

  return Promise.all([dispatch(requestShowListing(payload)), dispatch(fetchCurrentUser())])
    .then((response) => {
      const currentUser = getState().user.currentUser;
      if (currentUser?.stripeAccount) {
        dispatch(fetchStripeAccount());
      }
      return response;
    })
    .catch((e) => {
      throw e;
    });
};
